import {
  COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
  CollapsibleScreenHeader,
} from "@/components/prooftree/CollapsibleScreenHeader"
import { EmptyState } from "@/components/prooftree/EmptyState"
import { GlowField } from "@/components/prooftree/GlowField"
import { PressableScale } from "@/components/prooftree/PressableScale"
import { Box } from "@/components/ui/box"
import { HStack } from "@/components/ui/hstack"
import { Progress, ProgressFilledTrack } from "@/components/ui/progress"
import { Text } from "@/components/ui/text"
import { VStack } from "@/components/ui/vstack"
import { screenCopy } from "@/constants/Brand"
import { filterChipStyle } from "@/constants/ChipStyles"
import { Colors } from "@/constants/Colors"
import { useAuth } from "@/contexts/AuthContext"
import { CoachRecommendation, domains, getCoachRecommendations } from "@/data/prooftree"
import { getEqProofTreeProgress, type EqProofTreeProgress } from "@/services/prooftreeBackend"
import { buildProofTreeImmersion, getDomainImmersionState } from "@/services/prooftreeImmersion"
import { router } from "expo-router"
import {
  ChevronDown,
  ChevronRight,
  FileVideo,
  Filter,
  GitBranch,
  Lock,
  Plus,
  Search,
  Sparkles,
  Sprout,
} from "lucide-react-native"
import { ReactNode, useEffect, useRef, useState } from "react"
import { Animated, Easing, Platform, TextInput, TouchableOpacity, View } from "react-native"
import { SafeAreaView } from "react-native-safe-area-context"

const treeRecommendation = getCoachRecommendations("tree")[0]
const localTreeProgress: EqProofTreeProgress = {
  mode: "local",
  domains,
  message: "Using local ProofTree fallback.",
}

export default function SkillTreeScreen() {
  const auth = useAuth()
  const isSignedIn = Boolean(auth.session)
  const [expandedDomain, setExpandedDomain] = useState<string>("self_awareness")
  const [query, setQuery] = useState("")
  const [treeProgress, setTreeProgress] = useState<EqProofTreeProgress>(localTreeProgress)
  const scrollY = useRef(new Animated.Value(0)).current
  const treeDomains = treeProgress.domains
  const immersionState = buildProofTreeImmersion(treeDomains)
  const unlockedSkillIds = new Set(immersionState.unlockedSkillIds)
  const lockedPreviewSkillIds = new Set(immersionState.lockedPreviewSkillIds)
  const totalTreeLevels = treeDomains.reduce((sum, domain) => sum + domain.totalLevels, 0)
  const progressModeLabel =
    treeProgress.mode === "supabase" ? "Supabase progress" : "Local fallback"
  const domainResults = treeDomains
    .map((domain) => ({
      domain,
      visibleSkills: domain.skills.filter(
        (skill) =>
          (unlockedSkillIds.has(skill.id) || lockedPreviewSkillIds.has(skill.id)) &&
          `${skill.title} ${domain.title}`.toLowerCase().includes(query.toLowerCase()),
      ),
    }))
    .filter(({ visibleSkills }) => !query || visibleSkills.length > 0)

  useEffect(() => {
    let cancelled = false

    async function loadTreeProgress() {
      if (!isSignedIn) {
        setTreeProgress(localTreeProgress)
        return
      }

      const progress = await getEqProofTreeProgress()
      if (!cancelled) setTreeProgress(progress)
    }

    loadTreeProgress()

    return () => {
      cancelled = true
    }
  }, [auth.session?.access_token, isSignedIn])

  return (
    <SafeAreaView
      className="flex-1"
      edges={["top"]}
      style={{ backgroundColor: Colors.dark.surface }}
    >
      <GlowField />
      <CollapsibleScreenHeader
        scrollY={scrollY}
        eyebrow="EMO.coach ProofTree"
        title={screenCopy.tree.title}
        subtitle={screenCopy.tree.subtitle}
        rightAction={
          <TouchableOpacity
            className="h-11 w-11 items-center justify-center rounded-full"
            style={{ backgroundColor: Colors.dark.surfaceSecondary }}
          >
            <Filter color={Colors.dark.primary} size={20} />
          </TouchableOpacity>
        }
      />
      <Animated.ScrollView
        className="flex-1"
        contentContainerStyle={{
          padding: 24,
          paddingTop: COLLAPSIBLE_HEADER_CONTENT_TOP_PADDING,
          paddingBottom: Platform.OS === "web" ? 118 : 104,
        }}
        showsVerticalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], {
          useNativeDriver: false,
        })}
      >
        <Box
          className="mb-6 flex-row items-center rounded-2xl border px-4 py-3"
          style={{
            backgroundColor: Colors.dark.surfaceSecondary,
            borderColor: `${Colors.dark.primary}2E`,
          }}
        >
          <Search color={Colors.dark.textSecondary} size={18} />
          <TextInput
            className="ml-3 flex-1 text-white"
            placeholder="Search skills or domains"
            placeholderTextColor={Colors.dark.textSecondary}
            value={query}
            onChangeText={setQuery}
          />
        </Box>

        <Box
          className="mb-6 overflow-hidden rounded-3xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.primary}17`,
            borderColor: `${Colors.dark.primary}42`,
          }}
        >
          <View
            className="absolute -right-8 -top-10 h-28 w-28 rounded-full"
            style={{ backgroundColor: `${Colors.dark.primary}1F` }}
          />
          <HStack className="items-center justify-between gap-3">
            <VStack className="flex-1">
              <Text className="text-xs font-bold" style={{ color: Colors.dark.primary }}>
                EMO.coach CORE
              </Text>
              <Text className="text-lg font-bold text-white">
                {treeDomains.length} domains · {totalTreeLevels} coaching levels
              </Text>
              <Text className="mt-1 text-xs" style={{ color: Colors.dark.textSecondary }}>
                {progressModeLabel}
              </Text>
            </VStack>
            <HStack className="gap-2">
              <PressableScale
                className="h-10 w-10 items-center justify-center rounded-full"
                style={{ backgroundColor: `${Colors.dark.proof}1F` }}
                onPress={() => router.push("/proof/new")}
              >
                <FileVideo color={Colors.dark.proof} size={18} />
              </PressableScale>
              <PressableScale
                className="h-10 w-10 items-center justify-center rounded-full"
                style={{ backgroundColor: `${Colors.dark.growth}1F` }}
                onPress={() => router.push("/tasks")}
              >
                <Plus color={Colors.dark.growth} size={18} />
              </PressableScale>
            </HStack>
          </HStack>
        </Box>

        <TreeCoachCard recommendation={treeRecommendation} />

        <View className="mb-4 items-center">
          <Box
            className="rounded-full border px-5 py-2"
            style={{
              ...filterChipStyle,
              backgroundColor: `${Colors.dark.primary}1F`,
              borderColor: `${Colors.dark.primary}73`,
            }}
          >
            <Text className="font-bold" style={{ color: Colors.dark.primary }}>
              EQ Core
            </Text>
          </Box>
        </View>

        {domainResults.length > 0 ? (
          <View className="mb-5">
            <View className="flex-row flex-wrap justify-between gap-y-3">
              {domainResults.map(({ domain }, index) => {
                const Icon = domain.icon
                const expanded = expandedDomain === domain.id
                const domainImmersion = getDomainImmersionState(domain.id, immersionState)

                return (
                  <PressableScale
                    key={domain.id}
                    style={{ width: "48%" }}
                    onPress={() => setExpandedDomain(expanded ? "" : domain.id)}
                  >
                    <PulsingDomainCard color={domain.color} disabled={domain.progress >= 100}>
                      <Box
                        className="h-52 rounded-3xl border p-4"
                        style={{
                          backgroundColor: expanded
                            ? `${domain.color}20`
                            : Colors.dark.surfaceSecondary,
                          borderColor: expanded ? `${domain.color}CC` : `${domain.color}55`,
                          shadowColor: domain.color,
                          shadowOpacity: expanded ? 0.35 : 0.18,
                          shadowRadius: expanded ? 20 : 12,
                          shadowOffset: { width: 0, height: 10 },
                        }}
                      >
                        <HStack className="mb-5 items-start justify-between">
                          <Box
                            className="h-14 w-14 items-center justify-center rounded-2xl"
                            style={{ backgroundColor: `${domain.color}24` }}
                          >
                            <Icon color={domain.color} size={27} />
                          </Box>
                          <Box
                            className="rounded-full px-2 py-1"
                            style={{
                              backgroundColor: expanded
                                ? `${domain.color}30`
                                : Colors.dark.surfaceElevated,
                            }}
                          >
                            <Text className="text-xs font-bold" style={{ color: domain.color }}>
                              {index + 1}/4
                            </Text>
                          </Box>
                        </HStack>
                        <VStack className="flex-1 justify-between gap-4">
                          <VStack>
                            <Text className="text-lg font-bold leading-6 text-white">
                              {domain.title}
                            </Text>
                            <Text
                              className="mt-1 text-xs"
                              style={{ color: Colors.dark.textSecondary }}
                            >
                              {domain.completedLevels} of {domain.totalLevels} levels completed
                            </Text>
                            <Text className="mt-1 text-xs" style={{ color: domain.color }}>
                              Immersion {domainImmersion?.immersionLevel ?? 1}/4
                            </Text>
                          </VStack>
                          <VStack className="gap-2">
                            <Progress size="sm" value={domain.progress}>
                              <ProgressFilledTrack style={{ backgroundColor: domain.color }} />
                            </Progress>
                            <HStack className="items-center justify-between">
                              <Text
                                className="text-xs"
                                style={{ color: Colors.dark.textSecondary }}
                              >
                                {domain.progress}% coached
                              </Text>
                              <ChevronDown
                                color={domain.color}
                                size={18}
                                style={{ transform: [{ rotate: expanded ? "180deg" : "0deg" }] }}
                              />
                            </HStack>
                          </VStack>
                        </VStack>
                      </Box>
                    </PulsingDomainCard>
                  </PressableScale>
                )
              })}
            </View>
          </View>
        ) : (
          <View className="mb-5">
            <EmptyState
              icon={Search}
              title="Skill not found"
              description="Try another search or return to the full EMO.coach skill tree."
              actionLabel="Reset search"
              onAction={() => setQuery("")}
            />
          </View>
        )}

        {domainResults.map(({ domain, visibleSkills }) => {
          if (expandedDomain !== domain.id || visibleSkills.length === 0) {
            return null
          }

          const domainImmersion = getDomainImmersionState(domain.id, immersionState)

          return (
            <Box key={domain.id} className="mb-4">
              <HStack className="mb-3 items-center gap-3">
                <View className="h-px flex-1" style={{ backgroundColor: `${domain.color}55` }} />
                <HStack
                  className="items-center gap-2 rounded-full px-3 py-1"
                  style={{ backgroundColor: `${domain.color}18` }}
                >
                  <GitBranch color={domain.color} size={14} />
                  <Text className="text-sm font-bold" style={{ color: domain.color }}>
                    {domain.shortTitle} · Immersion {domainImmersion?.immersionLevel ?? 1}/4
                  </Text>
                </HStack>
                <View className="h-px flex-1" style={{ backgroundColor: `${domain.color}55` }} />
              </HStack>
              <View className="relative">
                <View
                  className="absolute bottom-8 left-[21px] top-7 w-px"
                  style={{ backgroundColor: `${domain.color}44`, pointerEvents: "none" }}
                />
                <VStack className="gap-3">
                  {visibleSkills.map((skill, index) => {
                    const locked = lockedPreviewSkillIds.has(skill.id)

                    return (
                      <PressableScale
                        key={skill.id}
                        onPress={() =>
                          router.push(`/skill/${skill.id}${locked ? "?locked=1" : ""}`)
                        }
                      >
                        <Box
                          className="rounded-2xl border p-4"
                          style={{
                            backgroundColor: locked
                              ? Colors.dark.surfaceSecondary
                              : Colors.dark.surfaceElevated,
                            borderColor: locked ? `${domain.color}33` : "rgba(255,255,255,0.06)",
                            opacity: locked ? 0.78 : 1,
                          }}
                        >
                          <HStack className="items-center gap-3">
                            <Box
                              className="h-11 w-11 items-center justify-center rounded-full border"
                              style={{
                                backgroundColor: `${domain.color}20`,
                                borderColor: `${domain.color}66`,
                              }}
                            >
                              {locked ? (
                                <Lock color={domain.color} size={17} />
                              ) : (
                                <Text className="text-xl">{index + 1}</Text>
                              )}
                            </Box>
                            <VStack className="flex-1 gap-2">
                              <HStack className="items-center justify-between gap-3">
                                <VStack className="flex-1">
                                  <Text className="font-bold text-white">{skill.title}</Text>
                                  {locked ? (
                                    <Text className="mt-1 text-xs" style={{ color: domain.color }}>
                                      Coming next
                                    </Text>
                                  ) : null}
                                </VStack>
                                <Text className="text-xs font-bold" style={{ color: domain.color }}>
                                  {locked
                                    ? `Stage ${skill.unlockStage}`
                                    : `${skill.completedLevels}/4`}
                                </Text>
                              </HStack>
                              <Progress size="xs" value={locked ? 0 : skill.progress}>
                                <ProgressFilledTrack style={{ backgroundColor: domain.color }} />
                              </Progress>
                            </VStack>
                          </HStack>
                        </Box>
                      </PressableScale>
                    )
                  })}
                </VStack>
              </View>
              {domainImmersion?.unlockReason ? (
                <Text
                  className="mt-3 text-xs leading-5"
                  style={{ color: Colors.dark.textSecondary }}
                >
                  {domainImmersion.unlockReason}
                </Text>
              ) : null}
            </Box>
          )
        })}
        <Box
          className="rounded-2xl border p-4"
          style={{
            backgroundColor: `${Colors.dark.primary}12`,
            borderColor: `${Colors.dark.primary}2E`,
          }}
        >
          <HStack className="items-start gap-3">
            <Sprout color={Colors.dark.primary} size={18} />
            <Text className="flex-1 text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              EMO.coach suggests: open a domain, choose one skill, and log proof for the nearest
              level.
            </Text>
          </HStack>
        </Box>
      </Animated.ScrollView>
    </SafeAreaView>
  )
}

function TreeCoachCard({ recommendation }: { recommendation: CoachRecommendation | undefined }) {
  if (!recommendation) return null

  return (
    <TouchableOpacity
      activeOpacity={0.86}
      onPress={() => router.push(recommendation.route as never)}
    >
      <Box
        className="mb-6 rounded-3xl border p-4"
        style={{
          backgroundColor: `${recommendation.accentColor}14`,
          borderColor: `${recommendation.accentColor}44`,
        }}
      >
        <HStack className="items-start gap-3">
          <Box
            className="h-11 w-11 items-center justify-center rounded-2xl"
            style={{ backgroundColor: `${recommendation.accentColor}22` }}
          >
            <Sparkles color={recommendation.accentColor} size={19} />
          </Box>
          <VStack className="flex-1 gap-1">
            <Text className="text-xs font-bold" style={{ color: recommendation.accentColor }}>
              RECOMMENDED BRANCH
            </Text>
            <Text className="text-lg font-bold text-white">{recommendation.title}</Text>
            <Text className="text-sm leading-5" style={{ color: Colors.dark.textSecondary }}>
              {recommendation.reason}
            </Text>
          </VStack>
          <ChevronRight color={recommendation.accentColor} size={20} />
        </HStack>
      </Box>
    </TouchableOpacity>
  )
}

function PulsingDomainCard({
  children,
  color,
  disabled,
}: {
  children: ReactNode
  color: string
  disabled: boolean
}) {
  const pulse = useRef(new Animated.Value(0)).current

  useEffect(() => {
    if (disabled) {
      return
    }

    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(pulse, {
          toValue: 1,
          duration: 1400,
          easing: Easing.out(Easing.quad),
          useNativeDriver: true,
        }),
        Animated.timing(pulse, {
          toValue: 0,
          duration: 1400,
          easing: Easing.in(Easing.quad),
          useNativeDriver: true,
        }),
      ]),
    )

    animation.start()
    return () => animation.stop()
  }, [disabled, pulse])

  const scale = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [1, 1.015],
  })
  const opacity = pulse.interpolate({
    inputRange: [0, 1],
    outputRange: [0.86, 1],
  })

  return (
    <Animated.View
      style={{
        opacity: disabled ? 1 : opacity,
        transform: [{ scale: disabled ? 1 : scale }],
        shadowColor: color,
        shadowOpacity: disabled ? 0.08 : 0.18,
        shadowRadius: disabled ? 8 : 14,
      }}
    >
      {children}
    </Animated.View>
  )
}
